/**
 * In-app attachment preview modal. Fetches an authenticated attachment as a blob (the endpoint
 * needs the auth header, so a plain <img>/<iframe> src can't reach it) and renders it inline:
 * images shown directly, PDFs through PdfView (inline, or the device's viewer on phones),
 * everything else falls back to a download button.
 *
 * The caller passes a `load` thunk returning the object URL + content type (e.g. wrapping
 * `fetchBlobUrl`); the object URL is revoked on close/unmount.
 */

import { useEffect, useState } from "react";

import { Modal } from "./Modal";
import { PdfView } from "./PdfView";

export interface AttachmentTarget {
  filename: string;
  load: () => Promise<{ url: string; contentType: string }>;
}

export function AttachmentPreview({
  target,
  onClose,
}: {
  target: AttachmentTarget | null;
  onClose: () => void;
}) {
  const [state, setState] = useState<
    | { phase: "loading" }
    | { phase: "ready"; url: string; contentType: string }
    | { phase: "error"; message: string }
  >({ phase: "loading" });

  useEffect(() => {
    if (!target) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setState({ phase: "loading" });
    target
      .load()
      .then(({ url, contentType }) => {
        objectUrl = url;
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setState({ phase: "ready", url, contentType });
      })
      .catch(() => {
        if (!cancelled) setState({ phase: "error", message: "Couldn't load this attachment." });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [target]);

  return (
    <Modal open={target !== null} title={target?.filename ?? "Attachment"} onClose={onClose} size="lg">
      {state.phase === "loading" && <p className="py-8 text-center text-sm text-gray-400">Loading…</p>}
      {state.phase === "error" && <p className="py-8 text-center text-sm text-red-600">{state.message}</p>}
      {state.phase === "ready" && <PreviewBody url={state.url} contentType={state.contentType} filename={target?.filename ?? "attachment"} />}
    </Modal>
  );
}

function PreviewBody({ url, contentType, filename }: { url: string; contentType: string; filename: string }) {
  if (contentType.startsWith("image/")) {
    return <img src={url} alt={filename} className="mx-auto max-h-[70dvh] w-auto rounded-lg" />;
  }
  if (contentType === "application/pdf") {
    return <PdfView url={url} filename={filename} className="h-[70dvh] w-full rounded-lg border" />;
  }
  return (
    <div className="py-8 text-center">
      <p className="mb-3 text-sm text-gray-500">This file type can't be previewed.</p>
      {/* A long filename wraps inside the button instead of running out of the dialog (RESP-09). */}
      <a
        href={url}
        download={filename}
        className="inline-block max-w-full rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg wrap-anywhere"
      >
        Download {filename}
      </a>
    </div>
  );
}
